import { type RefObject, useEffect, useRef } from 'react';
import { isRendered, openModalLayer, topModalLayer } from '../utils/modal-stack';

/** What can take focus inside a dialog. Disabled controls are skipped. */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function focusableIn(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.closest('[inert]') == null && el.getAttribute('aria-hidden') !== 'true',
  );
}

interface DialogFocusOptions {
  /** Escape. The caller decides what it means (close, or step back a screen). */
  onEscape: () => void;
  /**
   * Where focus goes when the dialog closes. Passed explicitly rather than
   * read from `document.activeElement` at open time, because the trigger can
   * be re-rendered while the dialog is up (a phase change moves the session
   * menu button from the top bar to the corner) and a remembered node would
   * then be a detached one.
   */
  returnFocusTo?: RefObject<HTMLElement | null>;
  /**
   * Step aside while ANOTHER modal is open outside this one: a gate raised on
   * top of a sheet (the agreement, the norms, the upgrade prompt) owns Escape
   * and Tab, and the sheet takes them back when it is gone. Off by default:
   * the circle session menu and breakout panel never sit under another modal.
   */
  yieldToOtherModals?: boolean;
  /**
   * Where focus lands on open, instead of the container: a destructive
   * confirm's safe action (Cancel), so an Enter pressed out of habit keeps
   * what it asked about. The dialog's name still reaches a screen reader
   * through `aria-labelledby` as focus enters it.
   */
  initialFocus?: RefObject<HTMLElement | null>;
  /**
   * With no `returnFocusTo`, give focus back to whatever held it when the
   * dialog opened (the ConfirmModal's callers pass no ref). The detached-node
   * caveat above still holds: an opener that has left the document is
   * skipped, as a ref's would be.
   */
  returnToOpener?: boolean;
  /**
   * The dialog sits OVER other trapped dialogs by design (the ConfirmModal,
   * raised from a sheet or the Giraffe dialog): listen on the window in the
   * capture phase, which runs before any listener on the document, and stop
   * the keys this trap handles there, so the dialog under it never sees a Tab
   * or an Escape meant for this one. Registration order cannot decide it: the
   * dialog underneath registered first.
   */
  onTop?: boolean;
  /**
   * Android's hardware Back over this dialog (utils/modal-stack.ts), when it
   * must differ from Escape. Leave it out and Back is Escape. The circle
   * agreement is the one case: Escape does nothing there (it is answered with
   * its buttons), but Back declines it, as it did before the stack (P5-1).
   */
  onBack?: () => void;
}

/** An `aria-modal` dialog other than `container`, outside it and not around it. */
function otherModalOpen(container: HTMLElement): boolean {
  return Array.from(document.querySelectorAll('[aria-modal="true"]')).some(
    (el) => el !== container && !container.contains(el) && !el.contains(container),
  );
}

/**
 * A modal dialog's keyboard contract, in one place: focus moves INTO the
 * dialog when it opens, Tab and Shift+Tab cycle inside it, Escape is the
 * caller's (unless a control inside it is `aria-expanded`, whose layer closes
 * first), and focus goes back to the trigger when it closes.
 *
 * The container gets focus first (give it `tabIndex={-1}` and an accessible
 * name), so a screen reader announces the dialog by name before any control in
 * it. Keydown is listened for on the document, not on the container, so the
 * trap holds even when focus has somehow landed outside it.
 */
export function useDialogFocus(
  open: boolean,
  containerRef: RefObject<HTMLElement | null>,
  {
    onEscape,
    returnFocusTo,
    yieldToOtherModals = false,
    initialFocus,
    returnToOpener = false,
    onTop = false,
    onBack,
  }: DialogFocusOptions,
): void {
  const escapeRef = useRef(onEscape);
  escapeRef.current = onEscape;
  const backRef = useRef(onBack);
  backRef.current = onBack;
  const returnRef = useRef(returnFocusTo);
  returnRef.current = returnFocusTo;
  const yieldRef = useRef(yieldToOtherModals);
  yieldRef.current = yieldToOtherModals;
  const initialRef = useRef(initialFocus);
  initialRef.current = initialFocus;
  const openerRef = useRef(returnToOpener);
  openerRef.current = returnToOpener;

  useEffect(() => {
    if (!open) return;
    // Captured now, for the return-focus check in the cleanup (see there).
    const outgoing = containerRef.current;
    // Read before focus moves: this is the opener.
    const opener =
      openerRef.current &&
      document.activeElement instanceof HTMLElement &&
      document.activeElement !== document.body
        ? document.activeElement
        : null;
    (initialRef.current?.current ?? outgoing)?.focus();

    // One layer on the modal stack while open: only the top layer answers a
    // key (one Escape closes one dialog), and Android Back answers it too.
    const layer = {
      back: () => (backRef.current ?? escapeRef.current)(),
      live: () => {
        const container = containerRef.current;
        if (!container || !container.isConnected || !isRendered(container)) return false;
        return !(yieldRef.current && otherModalOpen(container));
      },
    };
    const closeLayer = openModalLayer(layer);

    const onKeyDown = (event: KeyboardEvent) => {
      const container = containerRef.current;
      if (!container) return;
      if (topModalLayer() !== layer) return;
      if (event.key === 'Escape') {
        // A control inside the dialog with its own layer open (a popover, an
        // inline confirm) owns this Escape: it closes that layer, and the
        // dialog takes the next one. Stopping it here would close the whole
        // dialog and the layer's own listener would never hear it.
        if (container.querySelector('[aria-expanded="true"]')) return;
        event.preventDefault();
        event.stopPropagation();
        // A held Escape auto-repeats: only a fresh press closes, so a key held
        // through an in-flight request does not close the dialog the moment
        // it answers (b-020). The repeat is still swallowed here.
        if (event.repeat) return;
        escapeRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      if (onTop) event.stopPropagation();

      const items = focusableIn(container);
      if (items.length === 0) {
        event.preventDefault();
        container.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      const inside = active != null && container.contains(active);
      if (event.shiftKey) {
        if (!inside || active === first || active === container) {
          event.preventDefault();
          last.focus();
        }
      } else if (!inside || active === last || active === container) {
        event.preventDefault();
        first.focus();
      }
    };

    const target: Window | Document = onTop ? window : document;
    target.addEventListener('keydown', onKeyDown as EventListener, true);
    return () => {
      target.removeEventListener('keydown', onKeyDown as EventListener, true);
      closeLayer();
      // Back to whoever opened it — after the dialog has unmounted, so the
      // browser does not hand focus to <body> in between. NOT when ANOTHER
      // modal has taken focus in the meantime: the session menu hands over to
      // the breakout panel in one render, and pulling focus back to the
      // trigger would leave it outside the dialog now on screen.
      //
      // "Another" is the point. A dialog that leaves through an exit animation
      // (AnimatePresence) is still mounted, still `aria-modal`, and still holds
      // focus when this runs; treating it as "some modal has focus" gave up
      // every time and dropped focus to <body> once it unmounted (#949 backlog
      // item 1). Hence `outgoing`, captured when this dialog opened.
      const back = returnRef.current?.current ?? opener;
      if (back) {
        setTimeout(() => {
          // An opener that has left the document is not where focus goes: in
          // the 402 flow it is the prompt's "See plans", gone with the prompt,
          // and the prompt returns focus to ITS opener (review round 1 of
          // #1047, finding 3). Focusing a detached node does nothing useful
          // and would race whoever owns the return.
          if (!back.isConnected) return;
          const now = document.activeElement;
          const modal = now instanceof HTMLElement ? now.closest('[aria-modal="true"]') : null;
          if (modal && modal !== outgoing) return;
          back.focus();
        }, 0);
      }
    };
  }, [open, containerRef, onTop]);
}
